import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { IconWarehouse, IconPlus, IconLoader, IconAlert } from "../components/Icons";

interface Warehouse {
  id: string;
  code: string;
  name: string;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
  is_active: boolean;
}

const EMPTY_FORM = { code: "", name: "", address: "", city: "", postal_code: "", country: "CZ" };

export default function Warehouses() {
  const { user } = useCurrentUser();
  const canWrite = can(user, "warehouses", "write");
  const { t } = useTranslation();
  const [items, setItems] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/warehouses?include_inactive=true");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      if (editId) {
        await api.put(`/warehouses/${editId}`, form);
      } else {
        await api.post("/warehouses", form);
      }
      cancelForm();
      void load();
    } catch (e: any) {
      setErr(apiError(e, t(editId ? "warehouses.saveError" : "warehouses.createError")));
    }
  }

  function startEdit(w: Warehouse) {
    setEditId(w.id);
    setForm({
      code: w.code,
      name: w.name,
      address: w.address || "",
      city: w.city || "",
      postal_code: w.postal_code || "",
      country: w.country || "CZ",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelForm() {
    setShowForm(false);
    setEditId(null);
    setForm(EMPTY_FORM);
  }

  async function toggleActive(w: Warehouse) {
    setErr(null);
    try {
      await api.put(`/warehouses/${w.id}`, { is_active: !w.is_active });
      void load();
    } catch (e) {
      setErr(apiError(e, t("warehouses.saveError")));
    }
  }

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h1>{t("warehouses.title")}</h1>
          <p>{t("warehouses.subtitle")}</p>
        </div>
        {canWrite && (
        <button className="primary" onClick={() => (showForm ? cancelForm() : setShowForm(true))}>
          <IconPlus size={18} /> {showForm ? t("common.cancel") : t("warehouses.newWarehouse")}
        </button>
        )}
      </div>

      {err && (
        <div className="card" style={{
          padding: "12px 16px", marginBottom: 20,
          background: "var(--red-dim)", color: "var(--red)",
          display: "flex", alignItems: "center", gap: 8, fontSize: 14,
        }}>
          <IconAlert size={16} />
          {err}
        </div>
      )}

      {canWrite && showForm && (
        <div className="card" style={{ padding: 24, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>
            {editId ? t("warehouses.editWarehouse") : t("warehouses.newWarehouse")}
          </h3>
          <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            <div>
              <label>{t("warehouses.code")} *</label>
              <input placeholder={t("warehouses.codePlaceholder")} value={form.code} onChange={set("code")} required />
            </div>
            <div>
              <label>{t("warehouses.name")} *</label>
              <input placeholder={t("warehouses.namePlaceholder")} value={form.name} onChange={set("name")} required />
            </div>
            <div>
              <label>{t("warehouses.address")}</label>
              <input placeholder={t("warehouses.addressPlaceholder")} value={form.address} onChange={set("address")} />
            </div>
            <div>
              <label>{t("warehouses.city")}</label>
              <input placeholder={t("warehouses.cityPlaceholder")} value={form.city} onChange={set("city")} />
            </div>
            <div>
              <label>{t("warehouses.postalCode")}</label>
              <input placeholder={t("warehouses.postalCodePlaceholder")} value={form.postal_code} onChange={set("postal_code")} />
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12 }}>
              <button type="submit" className="primary">{editId ? t("common.save") : t("common.create")}</button>
              {editId && (
                <button type="button" className="btn-ghost" onClick={cancelForm}>{t("common.cancel")}</button>
              )}
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}>
          <IconLoader size={32} className="muted" />
        </div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}>
          <IconWarehouse size={48} className="muted" />
          <h3 style={{ marginTop: 16, fontSize: 16, fontWeight: 600 }}>{t("warehouses.emptyTitle")}</h3>
          <p style={{ color: "var(--muted)", marginTop: 8, fontSize: 14 }}>
            {t("warehouses.emptySubtitle")}
          </p>
        </div>
      ) : (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>{t("warehouses.name")}</th>
                <th>{t("warehouses.code")}</th>
                <th>{t("warehouses.address")}</th>
                <th>{t("warehouses.city")}</th>
                <th>{t("common.status")}</th>
                <th>{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.id}>
                  <td style={{ fontWeight: 600 }}>{w.name}</td>
                  <td className="mono">{w.code}</td>
                  <td>{w.address || "—"}</td>
                  <td>{w.city || "—"}</td>
                  <td>
                    <span className={w.is_active ? "badge badge-green" : "badge badge-red"}>
                      {w.is_active ? t("common.active") : t("common.inactive")}
                    </span>
                  </td>
                  <td>
                    {canWrite && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn-ghost" onClick={() => startEdit(w)}>
                        {t("common.edit")}
                      </button>
                      <button className="btn-ghost" onClick={() => toggleActive(w)}>
                        {w.is_active ? t("common.deactivate") : t("common.activate")}
                      </button>
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
